import { t } from '@lingui/core/macro';
import { X } from '@untitledui/icons/X';

import type { Gender } from '@/api/core-api/types.gen';

import { Button } from '@/components/base/buttons/button';
import { MultiSelectDropdown } from '@/components/base/select/multi-select-dropdown';
import { getGenderLabel } from '@/lib/matching-attributes/labels';

export const PARTICIPANT_GENDER_FILTER_VALUES = [
  'male',
  'female',
  'other',
  'nonbinary',
  'transmale',
  'transfemale',
  'prefernottoanswer',
] as const satisfies readonly Gender[];

export type ParticipantGenderFilter =
  (typeof PARTICIPANT_GENDER_FILTER_VALUES)[number];

export function nextSearchOnGenderFilterChange<
  T extends { page?: number; genders?: ParticipantGenderFilter[] | undefined },
>(prev: T, next: ReadonlySet<ParticipantGenderFilter>): T {
  return {
    ...prev,
    genders: next.size === 0 ? undefined : [...next],
    page: 1,
  };
}

interface ParticipantGenderFilterProps {
  value: ReadonlySet<ParticipantGenderFilter>;
  onChange: (next: Set<ParticipantGenderFilter>) => void;
}

export function ParticipantGenderFilter({
  value,
  onChange,
}: ParticipantGenderFilterProps) {
  const items = PARTICIPANT_GENDER_FILTER_VALUES.map((id) => ({
    id,
    label: getGenderLabel(id),
  }));

  return (
    <MultiSelectDropdown<ParticipantGenderFilter>
      size="xs"
      items={items}
      selectedKeys={value}
      onSelectionChange={onChange}
      triggerVariant="button"
      showCheckboxes
      placeholder={t`All genders`}
      triggerPrefix={t`Gender`}
      maxDisplay={2}
      noWrap
      matchTriggerWidth={false}
      popoverPlacement="bottom start"
      popoverClassName="min-w-[220px]"
      aria-label={t`Filter by gender`}
      footer={({ clearAll, close }) => (
        <Button
          color="tertiary"
          size="xs"
          iconLeading={X}
          className="w-full justify-start gap-2 px-3"
          onPress={() => {
            clearAll();
            close();
          }}
        >
          {t`Clear selection`}
        </Button>
      )}
    />
  );
}
